import React, { useState, useEffect, lazy, Suspense } from 'react';
import '../policies/PolicyStyles.css';
import '../printStyles.css';
import { exportToWord } from '../utils/exportToWord';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { incrementDocUsage } from '../utils/usage';
// ...existing imports...

// === Lazy imports (files load only when you click) ===
// (keep your existing lazy imports here...)
const ClientServiceAgreement = lazy(() => import('../documents/ClientServiceAgreement'));
const DriversHealthFitnessDeclaration = lazy(() => import('../documents/DriversHealthFitnessDeclaration'));
const EmployeeDetailsForm = lazy(() => import('../documents/EmployeeDetailsForm'));
const HGV_PSV_Driver_OfficeEmploymentContract = lazy(() => import('../documents/HGV_PSV_Driver_OfficeEmploymentContract'));
const MaintenanceContract = lazy(() => import('../documents/MaintenanceContract'));
const SubcontractorAgreementForNonDriverRoles = lazy(() => import('../documents/SubcontractorAgreementForNonDriverRoles'));
const SubcontractorSelfEmployedDriverAgreement = lazy(() => import('../documents/SubcontractorSelfEmployedDriverAgreement'));
const SupplierAgreement = lazy(() => import('../documents/SupplierAgreement'));
const TransportManagerContract = lazy(() => import('../documents/TransportManagerContract'));
const VehicleLeaseAgreement = lazy(() => import('../documents/VehicleLeaseAgreement'));

// Letters & Legal Correspondence
const ChangeOfTermsAndConditionsLetter = lazy(() => import('../documents/ChangeOfTermsAndConditionsLetter'));
const CommitmentToTransportRegulations = lazy(() => import('../documents/CommitmentToTransportRegulations'));
const ContractTerminationLetter = lazy(() => import('../documents/ContractTerminationLetter'));
const DisciplinaryOutcomeLetter = lazy(() => import('../documents/DisciplinaryOutcomeLetter'));
const DismissalLetter = lazy(() => import('../documents/DismissalLetter'));
const DriverRetentionLetter = lazy(() => import('../documents/DriverRetentionLetter'));
const DriverTrainingRecord = lazy(() => import('../documents/DriverTrainingRecord'));
const DriverWorkingHoursLog = lazy(() => import('../documents/DriverWorkingHoursLog'));
const DVSA_LetterResponseTemplate = lazy(() => import('../documents/DVSA_LetterResponseTemplate'));
const EmployeeSafetyAgreement = lazy(() => import('../documents/EmployeeSafetyAgreement'));
const FormalApologyLetterToClient = lazy(() => import('../documents/FormalApologyLetterToClient'));
const FormalCommunicationToTrafficCommissioner = lazy(() => import('../documents/FormalCommunicationToTrafficCommissioner'));
const IncidentReportForms = lazy(() => import('../documents/IncidentReportForms'));
const JobDescriptionTemplate = lazy(() => import('../documents/JobDescriptionTemplate'));
const LetterOfEmployment = lazy(() => import('../documents/LetterOfEmployment'));
const LetterOfWarning = lazy(() => import('../documents/LetterOfWarning'));
const LiabilityForGoodsInTransit = lazy(() => import('../documents/LiabilityForGoodsInTransit'));
const NewStarterWelcomeLetter = lazy(() => import('../documents/NewStarterWelcomeLetter'));
const NonDisclosureAgreement = lazy(() => import('../documents/NonDisclosureAgreement'));
const OfferOfEmploymentHGV_PSV_Office = lazy(() => import('../documents/OfferOfEmploymentHGV_PSV_Office'));
const NoticeOfDisciplinaryHearing = lazy(() => import('../documents/NoticeOfDisciplinaryHearing'));
const PaternityMaternityLeaveRequestForm = lazy(() => import('../documents/PaternityMaternityLeaveRequestForm'));
const PaymentTermsAndInvoicingProcedures = lazy(() => import('../documents/PaymentTermsAndInvoicingProcedures'));
const RedundancyLetter = lazy(() => import('../documents/RedundancyLetter'));
const ReferenceLetter = lazy(() => import('../documents/ReferenceLetter'));
const RequestForReference = lazy(() => import('../documents/RequestForReference'));
const ResignationAcceptanceLetter = lazy(() => import('../documents/ResignationAcceptanceLetter'));
const ReturnToWorkInterviewForm = lazy(() => import('../documents/ReturnToWorkInterviewForm'));
const TrainingCertificates = lazy(() => import('../documents/TrainingCertificates'));
const VehicleInspectionLog = lazy(() => import('../documents/VehicleInspectionLog'));

// === Registry (alphabetical within each section) ===
const CONTRACTS = [
  { key: 'ClientServiceAgreement', title: 'Client Service Agreement', Component: ClientServiceAgreement },
  { key: 'DriversHealthFitnessDeclaration', title: "Driver's Health and Fitness Declaration", Component: DriversHealthFitnessDeclaration },
  { key: 'EmployeeDetailsForm', title: 'Employee Details Form', Component: EmployeeDetailsForm },
  { key: 'HGV_PSV_Driver_OfficeEmploymentContract', title: 'HGV/PSV Driver / Office Employment Contract', Component: HGV_PSV_Driver_OfficeEmploymentContract },
  { key: 'MaintenanceContract', title: 'Maintenance Contract', Component: MaintenanceContract },
  { key: 'SubcontractorAgreementForNonDriverRoles', title: 'Subcontractor Agreement for Non-Driver Roles', Component: SubcontractorAgreementForNonDriverRoles },
  { key: 'SubcontractorSelfEmployedDriverAgreement', title: 'Sub-contractor / Self-employed Driver Agreement', Component: SubcontractorSelfEmployedDriverAgreement },
  { key: 'SupplierAgreement', title: 'Supplier Agreement', Component: SupplierAgreement },
  { key: 'TransportManagerContract', title: 'Transport Manager Contract', Component: TransportManagerContract },
  { key: 'VehicleLeaseAgreement', title: 'Vehicle Lease Agreement', Component: VehicleLeaseAgreement },
];

const LETTERS = [
  { key: 'ChangeOfTermsAndConditionsLetter', title: 'Change of Terms and Conditions Letter', Component: ChangeOfTermsAndConditionsLetter },
  { key: 'CommitmentToTransportRegulations', title: 'Commitment to Transport Regulations', Component: CommitmentToTransportRegulations },
  { key: 'ContractTerminationLetter', title: 'Contract Termination Letter', Component: ContractTerminationLetter },
  { key: 'DisciplinaryOutcomeLetter', title: 'Disciplinary Outcome Letter', Component: DisciplinaryOutcomeLetter },
  { key: 'DismissalLetter', title: 'Dismissal Letter', Component: DismissalLetter },
  { key: 'DriverRetentionLetter', title: 'Driver Retention Letter', Component: DriverRetentionLetter },
  { key: 'DriverTrainingRecord', title: 'Driver Training Record', Component: DriverTrainingRecord },
  { key: 'DriverWorkingHoursLog', title: 'Driver Working Hours Log', Component: DriverWorkingHoursLog },
  { key: 'DVSA_LetterResponseTemplate', title: 'DVSA Letter Response Template', Component: DVSA_LetterResponseTemplate },
  { key: 'EmployeeSafetyAgreement', title: 'Employee Safety Agreement', Component: EmployeeSafetyAgreement },
  { key: 'FormalApologyLetterToClient', title: 'Formal Apology Letter to a Client', Component: FormalApologyLetterToClient },
  { key: 'FormalCommunicationToTrafficCommissioner', title: 'Formal Communication to the Traffic Commissioner', Component: FormalCommunicationToTrafficCommissioner },
  { key: 'IncidentReportForms', title: 'Incident Report Forms', Component: IncidentReportForms },
  { key: 'JobDescriptionTemplate', title: 'Job Description Template', Component: JobDescriptionTemplate },
  { key: 'LetterOfEmployment', title: 'Letter of Employment', Component: LetterOfEmployment },
  { key: 'LetterOfWarning', title: 'Letter of Warning', Component: LetterOfWarning },
  { key: 'LiabilityForGoodsInTransit', title: 'Liability for Goods in Transit', Component: LiabilityForGoodsInTransit },
  { key: 'NewStarterWelcomeLetter', title: 'New Starter Welcome Letter', Component: NewStarterWelcomeLetter },
  { key: 'NonDisclosureAgreement', title: 'Non-Disclosure Agreement (NDA)', Component: NonDisclosureAgreement },
  { key: 'OfferOfEmploymentHGV_PSV_Office', title: 'Offer of Employment (HGV/PSV/Office)', Component: OfferOfEmploymentHGV_PSV_Office },
  { key: 'NoticeOfDisciplinaryHearing', title: 'Notice of Disciplinary Hearing', Component: NoticeOfDisciplinaryHearing },
  { key: 'PaternityMaternityLeaveRequestForm', title: 'Paternity / Maternity Leave Request Form', Component: PaternityMaternityLeaveRequestForm },
  { key: 'PaymentTermsAndInvoicingProcedures', title: 'Payment Terms and Invoicing Procedures', Component: PaymentTermsAndInvoicingProcedures },
  { key: 'RedundancyLetter', title: 'Redundancy Letter', Component: RedundancyLetter },
  { key: 'ReferenceLetter', title: 'Reference Letter', Component: ReferenceLetter },
  { key: 'RequestForReference', title: 'Request for Reference', Component: RequestForReference },
  { key: 'ResignationAcceptanceLetter', title: 'Resignation Acceptance Letter', Component: ResignationAcceptanceLetter },
  { key: 'ReturnToWorkInterviewForm', title: 'Return to Work Interview Form', Component: ReturnToWorkInterviewForm },
  { key: 'TrainingCertificates', title: 'Training Certificates', Component: TrainingCertificates },
  { key: 'VehicleInspectionLog', title: 'Vehicle Inspection Log', Component: VehicleInspectionLog },
];

const ALL = [...CONTRACTS, ...LETTERS];

// ...existing code...
export default function DocumentsPage() {
  const { user } = useAuth();

  const [clients, setClients] = React.useState([]);
  const [selectedClientId, setSelectedClientId] = React.useState('999');
  const [selectedKey, setSelectedKey] = React.useState(null); // <-- ADD THIS LINE

// Helper: open + track
const onOpenDoc = async (key, title, kind) => {
  try { if (user?.uid) await incrementDocUsage(user.uid, key, title, kind); } catch {}
  setSelectedKey(key);
};

  // Auto-open when coming from Home
  useEffect(() => {
    try {
      const raw = localStorage.getItem('openNextDoc');
      if (raw) {
        const { key, title, kind } = JSON.parse(raw);
        localStorage.removeItem('openNextDoc');
        if (key) {
          if (user?.uid) incrementDocUsage(user.uid, key, title, kind);
          setSelectedKey(key);
        }
      }
    } catch {}
  }, [user?.uid]);

  // Helper: find the selected client object (falls back to Blank)
  const selectedClient =
    clients.find(c => String(c.id) === String(selectedClientId)) ||
    { id: '999', name: '', companyNumber: '', address: '' };

  // ...rest of your component...

// === Firestore loading logic ===
useEffect(() => {
  if (!user) {
    // Not signed in: show only Blank Client
    setClients([{ id: '999', name: '', companyNumber: '', address: '' }]);
    setSelectedClientId('999');
    return;
  }

  (async () => {
    try {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const data = snap.data() || {};

      // Map profile clients to dropdown shape
      const list = (data.clients || []).map((c, idx) => ({
        id: String(idx + 1),
        name: c.companyName || '',
        companyNumber: c.companyNumber || '',
        address: c.address || '',
      }));

      // Append permanent Blank Client
      list.push({ id: '999', name: '', companyNumber: '', address: '' });

      setClients(list);

      // If current selection doesn’t exist anymore, choose first or blank
      const exists = list.some(c => String(c.id) === String(selectedClientId));
      if (!exists) {
        setSelectedClientId(list[0]?.id || '999');
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
      setClients([{ id: '999', name: '', companyNumber: '', address: '' }]);
      setSelectedClientId('999');
    }
  })();
}, [user?.uid]); // re-run when user changes


  const handleClientSelection = (e) => setSelectedClientId(e.target.value);
  const handlePrint = () => window.print();

  const found = ALL.find((i) => i.key === selectedKey);

  const handleExportToWord = () => {
    const el = document.getElementById('policy-content');
    if (el) {
      const fileSafe = (found?.title || 'document').replace(/\s+/g, '_').toLowerCase();
      exportToWord(el.innerHTML, `${fileSafe}.docx`);
    } else {
      alert('Policy content not found for export.');
    }
  };

  // Props passed to documents (auto-fill)
  const docProps = {
    clientName: selectedClient?.name,
    companyNumber: selectedClient?.companyNumber,
    clientAddress: selectedClient?.address,
    effectiveDate: new Date().toLocaleDateString('en-GB'),
  };

   // === List view ===
  if (!selectedKey) {
    return (
      <div className="p-4">
        {/* Contracts */}
        <div className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-center">Contracts & Agreements</h2>
          <div className="max-w-2xl mx-auto flex flex-col items-stretch space-y-4">
            {CONTRACTS.map(({ key, title }) => (
              <button
                key={key}
                type="button"
                onClick={() => onOpenDoc(key, title, 'contract')}
                className="w-full bg-blue-500 text-white font-bold p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg text-center"
                aria-label={`Open ${title}`}
              >
                {title}
              </button>
            ))}
          </div>
        </div>

        {/* Letters */}
        <div className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-center">Letters & Legal Correspondence</h2>
          <div className="max-w-2xl mx-auto flex flex-col items-stretch space-y-4">
            {LETTERS.map(({ key, title }) => (
              <button
                key={key}
                type="button"
                onClick={() => onOpenDoc(key, title, 'letter')}
                className="w-full bg-blue-500 text-white font-bold p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg text-center"
                aria-label={`Open ${title}`}
              >
                {title}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // === Viewer ===
  return (
    <div className="p-6 bg-white">
      <div className="no-print">
        {/* ========================= BACK BUTTON ========================= */}
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setSelectedKey(null)}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition"
          >
            ← Back to Documents
          </button>
        </div>
        {/* ======================= END BACK BUTTON ======================= */}

        {/* Client Selection Dropdown */}
        <div className="mt-4">
          <label className="block text-sm font-medium">Select Client:</label>
          <select
            className="mt-2 p-2 border border-gray-300 rounded-lg"
            value={selectedClientId}
            onChange={handleClientSelection}
          >
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name || '(Unnamed)'}{client.address ? ` - ${client.address}` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Display selected client details */}
        {selectedClient && selectedClient.name && (
          <div className="mt-6">
            <h3 className="text-2xl font-bold mb-4">Document: {found?.title || 'Document'}</h3>
            <h4 className="text-lg font-bold">Selected Client: {selectedClient.name}</h4>
            <p className="text-sm">Company Address: {selectedClient.address}</p>
          </div>
        )}

        {/* ======================= PRINT & EXPORT (page-level) ======================= */}
        <div className="mt-4 text-center print-button flex items-center justify-center gap-3 flex-wrap">
          <button
            onClick={handlePrint}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition"
          >
            Print Document
          </button>
          <button
            onClick={handleExportToWord}
            className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition"
          >
            Export to Word
          </button>
        </div>
        {/* =================== END PRINT & EXPORT (page-level) =================== */}
      </div>

      {/* Printable area */}
      <div className="printable-form" id="policy-content">
        <div className="mb-6">
          <Suspense fallback={<div className="p-3">Loading document…</div>}>
            {found ? <found.Component {...docProps} /> : <div>Document not found.</div>}
          </Suspense>
        </div>
        {/* Signature sections (optional) */}
        {/* ... add your signature JSX here if you want it at the page level ... */}
      </div>
    </div>
  );
}